import { SiteFooter } from "@/components/site-footer";
import { SiteNav } from "@/components/site-nav";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import siteData from "@/config/siteData.json";
import { auth } from "@/lib/auth";
import { db } from "@/lib/db.server";
import {
  getVisiblePlansForPlatform,
  PLAN_PLATFORM_WEB,
} from "@sendkit/shared/server";
import { generateMeta } from "@forge42/seo-tools/remix/metadata";
import { organization } from "@forge42/seo-tools/structured-data/organization";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  ArrowRight,
  Bot,
  Mail,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Terminal,
  Zap,
} from "lucide-react";
import { useState } from "react";
import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { Link, useLoaderData } from "react-router";

export const meta: MetaFunction = () => {
  const meta = generateMeta(
    {
      title: siteData.title,
      description: siteData.description,
      url: siteData.url,
      image: siteData.url + "/sendkit-logo.png",
      siteName: siteData.name as string,
    },
    [
      {
        "script:ld+json": organization({
          "@type": "Organization",
          name: siteData.name,
          description: siteData.description,
          url: siteData.url,
          logo: siteData.url + "/sendkit-logo.png",
          email: siteData.supportEmail,
        }),
      },
    ]
  );

  return meta;
};

export async function loader({ request }: LoaderFunctionArgs) {
  let session = await auth.api.getSession({
    headers: request.headers,
  });

  let isLoggedIn = false;

  if (session) {
    isLoggedIn = true;
  }

  const plans = await getVisiblePlansForPlatform(db, PLAN_PLATFORM_WEB);

  const allPlans = [
    {
      id: "free",
      name: "Free",
      bracket: "100",
      price_usd: 0,
      emails: 100,
      stripe_price_id: null,
    },
    ...plans,
  ];

  return { isLoggedIn, plans: allPlans };
}

export default function LandingPage() {
  const { isLoggedIn, plans } = useLoaderData<typeof loader>();
  const [selectedPlan, setSelectedPlan] = useState(plans[0]);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteNav isLoggedIn={isLoggedIn} />
      <main className="flex-1">
        <section className="container grid lg:grid-cols-2 gap-6 pt-16 pb-8 md:pt-24">
          <div className="flex flex-col justify-center space-y-8">
            <div className="space-y-6">
              <h1 className="text-5xl font-bold leading-tight tracking-tighter md:text-6xl lg:leading-[1.1]">
                Email marketing doesn't have to be complex.
              </h1>
              <h2 className="text-2xl text-muted-foreground md:text-3xl">
                Too many features? Confusing metrics? Spending hours on design?
                Email shouldn't be this hard.
              </h2>
            </div>
            <div className="flex flex-col gap-4 min-[400px]:flex-row">
              <Link to="/signup">
                <Button size="lg" className="h-12">
                  Get Started
                </Button>
              </Link>
              <Link to={siteData.installUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="lg" className="h-12">
                  Install on Shopify
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative w-full lg:block overflow-hidden rounded-xl">
            <DotLottieReact
              src="https://lottie.host/8c4c1067-4b35-4d71-9fcd-9ed4492c9cfc/8iJmP1KIq2.lottie"
              loop
              autoplay
            />
          </div>
        </section>
        <section className="container py-20" id="features">
          <div className="mx-auto text-center max-w-[900px] space-y-4">
            <h2 className="text-4xl font-bold">Your emails need SendKit.</h2>
            <p className="text-xl text-muted-foreground">
              SendKit frees you up. SendKit gets you in the Inbox. It's all
              about making email marketing simple.
            </p>
          </div>
          <div className="grid gap-8 mt-16 md:grid-cols-3">
            <div className="space-y-4">
              <h3 className="text-xl font-bold">Simple Templates Blocks</h3>
              <p className="text-muted-foreground">
                Pre-built templates blocks that look great on every device. Just
                add your content and hit send.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-bold">Clear Analytics</h3>
              <p className="text-muted-foreground">
                See what matters at a glance. No PhD in data science required.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-bold">Fair Pricing</h3>
              <p className="text-muted-foreground">
                Pay for what you send. No hidden fees, no surprises.
              </p>
            </div>
          </div>
        </section>
        <section className="container py-20" id="shopify">
          <div className="mx-auto text-center max-w-[900px] space-y-4">
            <h2 className="text-4xl font-bold">
              Everything you need to ship newsletters inside Shopify
            </h2>
            <p className="text-xl text-muted-foreground">
              Build, personalize, and send with live Shopify data
            </p>
          </div>
          <div className="grid gap-8 mt-16 md:grid-cols-3">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold">Live Shopify sync</h3>
              </div>
              <p className="text-muted-foreground">
                Products, customers, and discounts stay in sync so every send is
                accurate and on-brand.
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold">In-app newsletter editor</h3>
              </div>
              <p className="text-muted-foreground">
                Drag-and-drop blocks, saved templates, and Shopify product pulls
                built right into the app.
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Zap className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold">Performance visibility</h3>
              </div>
              <p className="text-muted-foreground">
                Campaign analytics, deliverability insights, and growth metrics
                viewable alongside your store performance.
              </p>
            </div>
          </div>
        </section>
        <section className="border-t bg-muted/50">
          <div className="container grid gap-8 py-16 md:grid-cols-3">
            <div className="space-y-3">
              <p className="text-sm font-semibold text-primary">Create</p>
              <h3 className="text-2xl font-bold">Move faster with templates</h3>
              <p className="text-muted-foreground">
                Start from proven newsletter layouts, pull products in one
                click, and save house styles so every send feels consistent.
              </p>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold text-primary">Collaborate</p>
              <h3 className="text-2xl font-bold">
                Approve and schedule together
              </h3>
              <p className="text-muted-foreground">
                Preview on desktop and mobile, share for review, and schedule or
                send immediately without switching tabs.
              </p>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold text-primary">Grow</p>
              <h3 className="text-2xl font-bold">See what drives revenue</h3>
              <p className="text-muted-foreground">
                Track opens, clicks, and attributed sales in one place so you
                know which campaigns push the store forward.
              </p>
            </div>
          </div>
          <div className="container pb-16 flex justify-center">
            <Link to={siteData.installUrl} target="_blank" rel="noreferrer">
              <Button size="lg" className="h-12">
                Install App
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
        <section id="ai-agents" className="border-t bg-muted/50">
          <div className="container py-20">
            <div className="mx-auto max-w-[900px] text-center space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border px-4 py-1 text-sm">
                <Bot className="h-4 w-4" />
                SendKit MCP
              </div>
              <h2 className="text-4xl font-bold">
                Your AI agent can run email marketing.
              </h2>
              <p className="text-xl text-muted-foreground">
                Connect Claude, ChatGPT, or any MCP client. Agents design
                on-brand newsletters, preview them, and schedule campaigns with
                your approval.
              </p>
            </div>
            <div className="grid gap-8 mt-16 md:grid-cols-3">
              <div className="space-y-3">
                <Sparkles className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold">Brand-aware design</h3>
                <p className="text-muted-foreground">
                  Agents read your theme, logo, header, and footer, then build
                  structured newsletters that stay editable in SendKit.
                </p>
              </div>
              <div className="space-y-3">
                <Terminal className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold">Preview before sending</h3>
                <p className="text-muted-foreground">
                  Render drafts, check warnings and spam scores, and send tests
                  before anything reaches your audience.
                </p>
              </div>
              <div className="space-y-3">
                <ShieldCheck className="h-6 w-6 text-primary" />
                <h3 className="text-xl font-bold">
                  Human-approved scheduling
                </h3>
                <p className="text-muted-foreground">
                  Scheduling requires explicit confirmation and returns the
                  recipient count and quota impact first.
                </p>
              </div>
            </div>
            <div className="mt-12 grid gap-8 lg:grid-cols-2 lg:items-center">
              <div className="rounded-xl border bg-background p-6">
                <p className="text-sm font-semibold text-muted-foreground mb-3">
                  MCP endpoint
                </p>
                <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                  {`${siteData.url}/mcp
Authorization: Bearer pk_your_public_key`}
                </pre>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-bold">
                  Works with the assistants you already use
                </h3>
                <p className="text-muted-foreground">
                  The remote MCP server exposes newsletter, brand, segment,
                  template, product, quota, and analytics tools with scoped API
                  keys and human-approved scheduling.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg">
                    <Link to="/docs/mcp">
                      Read the MCP docs
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <Link
                      to={
                        isLoggedIn ? "/dashboard/settings/api" : "/signup"
                      }
                    >
                      {isLoggedIn ? "Create an API key" : "Get started"}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="pricing" className="container py-20">
          <div className="mx-auto text-center max-w-[900px] space-y-4 mb-12">
            <h2 className="text-4xl font-bold">Simple, Transparent Pricing</h2>
            <p className="text-xl text-muted-foreground">
              Select your monthly email send volume
            </p>
          </div>

          <Card className="w-full max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="text-3xl font-bold text-center">
                {selectedPlan.name} Plan
              </CardTitle>
              <CardDescription>
                Select your monthly email send volume
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                onValueChange={(value) =>
                  setSelectedPlan(
                    plans.find((plan) => plan.bracket === value) || plans[0]
                  )
                }
                value={selectedPlan.bracket}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select email send bracket" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.bracket} value={plan.bracket}>
                      {plan.name} ({plan.bracket} emails)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="mt-6 text-center">
                <p className="text-4xl font-bold">${selectedPlan.price_usd}</p>
                <p className="text-gray-600">per month</p>
              </div>

              <div className="mt-6">
                <h3 className="font-semibold mb-2">Plan includes:</h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Up to {selectedPlan.emails.toLocaleString()} email sends per
                    month
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Unlimited subscribers
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Advanced analytics
                  </li>
                  <li className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    24/7 support
                  </li>
                </ul>
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild className="w-full">
                <Link to={isLoggedIn ? "/dashboard/plans" : "/signup"}>
                  {isLoggedIn ? "Upgrade Plan" : "Get Started"}
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </section>
        <section className="border-t bg-muted/50">
          <div className="container py-20">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold">
                  Ready to simplify your email marketing?
                </h2>
                <p className="text-xl text-muted-foreground">
                  Switch to SendKit and get started today.
                </p>
              </div>
              <div className="flex items-center justify-end">
                <Link to="/signup">
                  <Button size="lg" className="h-12">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
